<script lang="ts">
	import { goto } from '$app/navigation';
	import { resolve } from '$app/paths';
	import ArrowIcon from '$lib/components/marketing/redesign2025/ArrowIcon.svelte';
	import JobProposalCard from '$lib/components/marketing/redesign2025/JobProposalCard.svelte';
	import PageHero from '$lib/components/marketing/redesign2025/PageHero.svelte';
	import VoucherInput, { type AppliedVoucher } from '$lib/components/vouchers/VoucherInput.svelte';
	import { m } from '$lib/paraglide/messages.js';
	import { getLocale, localizeHref } from '@/paraglide/runtime';
	import {
		CapitansStatusOptions,
		CapitansTitleNameOptions,
		Collections,
		UsersRoleOptions,
		UsersStatusOptions,
		type CapitansTitleNameOptions as CapitansTitleName
	} from '@/pocketbase/pocketbase-types';
	import { currentUser, getErrorMessage, pb } from '@/utils/ApiClient';
	import CircleAlert from '@lucide/svelte/icons/circle-alert';
	import Eye from '@lucide/svelte/icons/eye';
	import EyeOff from '@lucide/svelte/icons/eye-off';
	import ShipWheel from '@lucide/svelte/icons/ship-wheel';
	import { ClientResponseError } from 'pocketbase';
	import posthog from 'posthog-js';
	import { onMount } from 'svelte';

	const TITLE_OPTIONS = Object.values(CapitansTitleNameOptions) as CapitansTitleName[];

	type FormError =
		| 'missing_title'
		| 'already_capitan'
		| 'terms'
		| 'password'
		| 'existing_account'
		| 'generic'
		| null;

	const locale = $derived(getLocale());

	const inputClass =
		'rounded-[10px] border border-white/16 bg-white/7 px-3.5 py-3 font-sans text-base text-white placeholder:text-zp-paper/40 focus:border-white/45 focus:outline-none disabled:opacity-60';

	const pillars = $derived.by(() => {
		void locale;
		return [
			{
				icon: 'users' as const,
				title: m.cap_p1_title(),
				body: m.cap_p1_body()
			},
			{
				icon: 'sun' as const,
				title: m.cap_p2_title(),
				body: m.cap_p2_body()
			},
			{
				icon: 'check' as const,
				title: m.cap_p3_title(),
				body: m.cap_p3_body()
			}
		];
	});

	const steps = $derived.by(() => {
		void locale;
		return [
			{ n: '01', title: m.cap_step1_title(), body: m.cap_step1_body() },
			{ n: '02', title: m.cap_step2_title(), body: m.cap_step2_body() },
			{ n: '03', title: m.cap_step3_title(), body: m.cap_step3_body() },
			{ n: '04', title: m.cap_step4_title(), body: m.cap_step4_body() }
		];
	});

	let loading = $state(false);
	let formError = $state<FormError>(null);
	let formErrorDetail = $state('');

	const errorTitle = $derived.by(() => {
		switch (formError) {
			case 'missing_title':
				return m.cap_err_missing_data();
			case 'already_capitan':
				return m.cap_err_already_title();
			case 'terms':
				return m.cap_err_terms_title();
			case 'password':
				return m.cap_err_password_title();
			case 'existing_account':
				return m.cap_err_existing_title();
			case 'generic':
				return m.cap_err_register_title();
			default:
				return '';
		}
	});

	const errorMessage = $derived.by(() => {
		switch (formError) {
			case 'missing_title':
				return m.cap_err_missing_title_msg();
			case 'already_capitan':
				return m.cap_err_already_msg();
			case 'terms':
				return m.cap_err_terms_msg();
			case 'password':
				return m.grassy_brave_yak_agree();
			case 'existing_account':
				return m.cap_err_existing_msg();
			case 'generic':
				return formErrorDetail;
			default:
				return '';
		}
	});

	let emailAddress = $state('');
	let password = $state('');
	let showPassword = $state(false);
	let isAuthenticated = $state(pb.authStore.isValid);
	let acceptTerms = $state(false);
	let acceptNewsletter = $state(false);
	let appliedVoucher = $state<AppliedVoucher | null>(null);

	let titleName = $state('');

	const passwordValid = $derived(password.length >= 10);

	onMount(() => {
		return currentUser.subscribe(() => {
			isAuthenticated = pb.authStore.isValid;
		});
	});

	function isUniqueConstraintError(error: unknown): boolean {
		if (!(error instanceof ClientResponseError) || !error.response?.data) return false;
		return Object.values(error.response.data).some((data: unknown) => {
			if (!data || typeof data !== 'object' || !('message' in data)) return false;
			const msg = (data as { message?: string }).message;
			return typeof msg === 'string' && msg.includes('must be unique');
		});
	}

	async function ensureNoExistingCapitan(userId: string): Promise<boolean> {
		const res = await pb.collection(Collections.Capitans).getList(1, 1, {
			filter: `user="${userId}"`
		});
		return res.totalItems === 0;
	}

	async function createCapitanRecord(userId: string, titulacion: CapitansTitleName) {
		await pb.collection(Collections.Capitans).create({
			user: userId,
			status: CapitansStatusOptions.pending,
			titleName: titulacion,
			...(appliedVoucher?.code ? { voucherCode: appliedVoucher.code } : {})
		});
		posthog.capture('capitan_signed_up', {
			userId,
			titleName: titulacion,
			email: pb.authStore.record?.email,
			...(appliedVoucher?.code ? { voucherCode: appliedVoucher.code } : {})
		});
	}

	async function finishAndRedirect(userId: string, titulacion: CapitansTitleName) {
		await createCapitanRecord(userId, titulacion);
		goto(resolve(localizeHref('/cuenta') as '/cuenta'));
	}

	async function registerWithPasswordAndCreateCapitan(
		email: string,
		titulacion: CapitansTitleName
	) {
		const username = email.split('@')[0];
		await pb.collection(Collections.Users).create({
			email,
			username,
			password,
			passwordConfirm: password,
			lastLogin: new Date(),
			role: UsersRoleOptions.user,
			status: UsersStatusOptions.active,
			acceptNewsletter
		});

		posthog.identify(email, { email });
		posthog.capture('user_signed_up', { email, source: 'registro_capitan' });

		await pb.collection(Collections.Users).requestVerification(email);
		await pb.collection(Collections.Users).authWithPassword(email, password);

		const uid = pb.authStore.record?.id;
		if (!uid) throw new Error('No se pudo obtener el usuario tras el registro');

		await finishAndRedirect(uid, titulacion);
	}

	async function handleSubmit(e: Event) {
		e.preventDefault();
		try {
			loading = true;
			formError = null;
			formErrorDetail = '';

			const email = emailAddress.trim().toLowerCase();
			const titulacion = titleName as CapitansTitleName;

			if (!titleName) {
				formError = 'missing_title';
				return;
			}

			if (isAuthenticated && pb.authStore.record?.id) {
				const uid = pb.authStore.record.id;
				if (!(await ensureNoExistingCapitan(uid))) {
					formError = 'already_capitan';
					return;
				}
				await finishAndRedirect(uid, titulacion);
				return;
			}

			if (!acceptTerms) {
				formError = 'terms';
				return;
			}

			if (!passwordValid) {
				formError = 'password';
				return;
			}

			await registerWithPasswordAndCreateCapitan(email, titulacion);
		} catch (error) {
			console.error(error);
			if (isUniqueConstraintError(error)) {
				formError = 'existing_account';
			} else {
				formError = 'generic';
				formErrorDetail = getErrorMessage(error as ClientResponseError);
			}
		} finally {
			loading = false;
		}
	}
</script>

<PageHero
	eyebrow={m.cap_hero_eyebrow()}
	breadcrumbLabel={m.cap_hero_breadcrumb()}
	lead={m.cap_hero_lead()}
>
	{#snippet heroImage(className)}
		<enhanced:img
			src="/static/img/landing/redesign-2025/photos/crew-cies.avif"
			alt=""
			class={className}
			fetchpriority="high"
			sizes="100vw"
		></enhanced:img>
	{/snippet}
	{m.cap_hero_h1_before()}<em class="italic">{m.cap_hero_h1_em()}</em>
</PageHero>

<section class="py-20 md:py-[100px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="mb-14 grid items-start gap-9 lg:grid-cols-[0.9fr_1.1fr] lg:gap-[70px]">
			<div>
				<span
					class="mb-5 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
					>{m.cap_pillar_eyebrow()}</span
				>
				<h2 class="text-[clamp(1.9rem,3.4vw,3rem)] leading-[1.06]">
					{m.cap_pillar_h2()}
				</h2>
			</div>
			<p class="m-0 text-[1.1rem] text-zp-prose">
				{m.cap_pillar_intro()}
			</p>
		</div>
		<div class="grid gap-7 md:grid-cols-3">
			{#each pillars as pillar (pillar.title)}
				<div class="rounded-2xl border border-zp-line bg-white px-8 py-[34px]">
					<div class="mb-[22px] size-10 text-zp-accent">
						{#if pillar.icon === 'users'}
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="1.6"
								stroke-linecap="round"
								stroke-linejoin="round"
								class="size-full"
								aria-hidden="true"
							>
								<path d="M16 8a4 4 0 10-8 0M3 21v-1a6 6 0 0112 0v1M18 14a5 5 0 013 4.6V21" />
							</svg>
						{:else if pillar.icon === 'sun'}
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="1.6"
								stroke-linecap="round"
								stroke-linejoin="round"
								class="size-full"
								aria-hidden="true"
							>
								<path d="M12 3v3M12 18v3M5 12H2M22 12h-3M6 6l-2-2M18 6l2-2M6 18l-2 2M18 18l2 2" />
								<circle cx="12" cy="12" r="4" />
							</svg>
						{:else}
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="1.6"
								stroke-linecap="round"
								stroke-linejoin="round"
								class="size-full"
								aria-hidden="true"
							>
								<path d="M20 6 9 17l-5-5" />
								<path d="M3 12a9 9 0 109-9" />
							</svg>
						{/if}
					</div>
					<h3 class="mb-3 text-[1.3rem] leading-snug">{pillar.title}</h3>
					<p class="m-0 text-[0.98rem] text-zp-muted">{pillar.body}</p>
				</div>
			{/each}
		</div>
	</div>
</section>

<section class="bg-zp-paper2 py-20 md:py-[100px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="grid items-center gap-10 lg:grid-cols-[1fr_1.05fr] lg:gap-[70px]">
			<div>
				<span
					class="mb-5 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
					>{m.cap_prop_eyebrow()}</span
				>
				<h2 class="text-[clamp(1.9rem,3.4vw,3rem)] leading-[1.06]">{m.cap_prop_h2()}</h2>
				<div class="mt-6 space-y-5 text-[1.1rem] text-zp-prose">
					<p class="m-0">
						{m.cap_prop_p1_before()}<b class="text-zp-ink">{m.cap_prop_p1_bold()}</b
						>{m.cap_prop_p1_after()}
					</p>
					<p class="m-0">
						{m.cap_prop_p2()}
					</p>
				</div>
			</div>
			<JobProposalCard />
		</div>
	</div>
</section>

<section class="py-20 md:py-[100px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="mb-14 grid items-start gap-9 lg:grid-cols-[0.9fr_1.1fr] lg:gap-[70px]">
			<div>
				<span
					class="mb-5 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
					>{m.cap_steps_eyebrow()}</span
				>
				<h2 class="text-[clamp(1.9rem,3.4vw,3rem)] leading-[1.06]">
					{m.cap_steps_h2()}
				</h2>
			</div>
			<p class="m-0 text-[1.1rem] text-zp-prose">
				{m.cap_steps_intro()}
			</p>
		</div>
		<div class="grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
			{#each steps as step (step.n)}
				<div class="rounded-2xl border border-zp-line bg-white px-8 py-[34px]">
					<div class="mb-8 font-serif text-[1.05rem] text-zp-accent">{step.n}</div>
					<h3 class="mb-3 text-[1.3rem] leading-snug">{step.title}</h3>
					<p class="m-0 text-[0.98rem] text-zp-muted">{step.body}</p>
				</div>
			{/each}
		</div>
	</div>
</section>

<section id="registro" class="bg-zp-paper2 py-20 md:py-[100px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<form
			class="grid items-start gap-9 rounded-3xl bg-zp-navy p-8 text-zp-paper md:grid-cols-[1fr_1.1fr] md:gap-[60px] md:p-14"
			onsubmit={handleSubmit}
			autocomplete={isAuthenticated ? 'on' : 'off'}
		>
			<div>
				<span
					class="mb-4 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-paper/60 uppercase"
					>{m.cap_form_eyebrow()}</span
				>
				<h2 class="text-[clamp(1.8rem,3vw,2.6rem)] leading-[1.06] text-white">
					{m.cap_form_h2()}
				</h2>
				<p class="mt-4 text-[1.02rem] text-zp-paper/78">
					{#if isAuthenticated}
						{m.cap_form_auth_before()}<span class="font-medium text-white"
							>{pb.authStore.record?.email}</span
						>.
					{:else}
						{m.cap_form_guest_p()}
					{/if}
				</p>
			</div>

			<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<div class="flex flex-col gap-1.5 sm:col-span-2">
					<label
						for="cap-title"
						class="text-[0.74rem] font-semibold tracking-widest text-zp-paper/55 uppercase"
					>
						{m.cap_label_title()}
					</label>
					<select
						id="cap-title"
						bind:value={titleName}
						required
						disabled={loading}
						class="{inputClass} w-full"
					>
						<option value="" disabled>{m.cap_select_placeholder()}</option>
						{#each TITLE_OPTIONS as titulacion (titulacion)}
							<option value={titulacion}>{titulacion}</option>
						{/each}
					</select>
					<p class="text-[0.8rem] text-zp-paper/55">
						{m.cap_title_hint()}
					</p>
				</div>

				{#if !isAuthenticated}
					<div class="flex flex-col gap-1.5">
						<label
							for="cap-email"
							class="text-[0.74rem] font-semibold tracking-widest text-zp-paper/55 uppercase"
						>
							{m.lofty_gross_larva_win()}
						</label>
						<input
							id="cap-email"
							type="email"
							bind:value={emailAddress}
							placeholder={m.cap_email_placeholder()}
							autocomplete="email"
							required
							disabled={loading}
							class={inputClass}
						/>
					</div>

					<div class="flex flex-col gap-1.5">
						<label
							for="cap-password"
							class="text-[0.74rem] font-semibold tracking-widest text-zp-paper/55 uppercase"
						>
							{m.cap_label_password()}
						</label>
						<div class="relative">
							<input
								id="cap-password"
								type={showPassword ? 'text' : 'password'}
								bind:value={password}
								autocomplete="new-password"
								required
								disabled={loading}
								class="{inputClass} w-full pr-10"
							/>
							<button
								type="button"
								class="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-zp-paper/55 hover:text-zp-paper"
								onclick={() => (showPassword = !showPassword)}
								aria-label={showPassword ? m.cap_hide_password() : m.cap_show_password()}
							>
								{#if showPassword}
									<EyeOff class="size-4" />
								{:else}
									<Eye class="size-4" />
								{/if}
							</button>
						</div>
						<p class="text-[0.8rem] text-zp-paper/55">{m.cap_password_hint()}</p>
					</div>

					<div class="flex items-start gap-2.5 sm:col-span-2">
						<input
							type="checkbox"
							id="acceptTermsCap"
							bind:checked={acceptTerms}
							required
							disabled={loading}
							class="mt-1 size-4 shrink-0 accent-zp-paper"
						/>
						<label for="acceptTermsCap" class="text-[0.88rem] leading-snug text-zp-paper/78">
							{m.elegant_loud_iguana_slurp()}
							<a
								href={resolve(localizeHref('/terminos-y-condiciones') as '/terminos-y-condiciones')}
								target="_blank"
								rel="noopener noreferrer"
								class="underline hover:text-white">{m.few_legal_carp_lend()}</a
							>
							{m.east_loose_leopard_dream()}
							<a
								href={resolve(
									localizeHref('/politica-de-privacidad/') as '/politica-de-privacidad'
								)}
								target="_blank"
								rel="noopener noreferrer"
								class="underline hover:text-white">{m.each_basic_dachshund_value()}</a
							>
						</label>
					</div>

					<div class="flex items-start gap-2.5 sm:col-span-2">
						<input
							type="checkbox"
							id="acceptNewsletterCap"
							bind:checked={acceptNewsletter}
							disabled={loading}
							class="mt-1 size-4 shrink-0 accent-zp-paper"
						/>
						<label for="acceptNewsletterCap" class="text-[0.88rem] text-zp-paper/78">
							{m.mellow_pretty_rat_earn()}
						</label>
					</div>
				{/if}

				<div class="sm:col-span-2">
					<VoucherInput
						context="capitan"
						bind:applied={appliedVoucher}
						disabled={loading}
						tone="dark"
					/>
					{#if appliedVoucher}
						<p class="mt-1.5 text-[0.8rem] text-zp-paper/70">
							{#if appliedVoucher.discountType === 'percent'}
								Código <span class="font-medium text-zp-paper/90"
									>{appliedVoucher.code.toUpperCase()}</span
								>
								activo: {appliedVoucher.discountValue} puntos menos de comisión (~{Math.round(
									(appliedVoucher.discountValue / 15) * 100
								)}% menos sobre el 15% habitual).
							{:else}
								Código <span class="font-medium text-zp-paper/90"
									>{appliedVoucher.code.toUpperCase()}</span
								>
								activo: −{appliedVoucher.discountValue}€ en tu comisión por salida.
							{/if}
						</p>
					{/if}
				</div>

				{#if formError}
					<div
						class="flex gap-3 rounded-[10px] border border-red-300/40 bg-red-950/30 px-4 py-3 sm:col-span-2"
						role="alert"
					>
						<CircleAlert class="mt-0.5 size-4 shrink-0 text-red-200" />
						<div class="text-[0.9rem] text-red-100">
							<p class="font-semibold text-white">{errorTitle}</p>
							<p class="mt-1">
								{errorMessage}
								{#if formError === 'existing_account'}
									<a
										href={resolve(localizeHref('/iniciar-sesion') as '/iniciar-sesion')}
										class="ml-1 font-medium underline hover:text-white"
										>{m.new_upper_thrush_flow()}</a
									>
								{/if}
							</p>
						</div>
					</div>
				{/if}

				<button
					type="submit"
					disabled={loading || (!isAuthenticated && !acceptTerms)}
					class="col-span-1 mt-1.5 inline-flex items-center justify-center gap-2 rounded-[40px] border border-transparent bg-zp-paper px-6 py-3.5 text-[0.86rem] font-semibold text-zp-navy transition-all hover:-translate-y-px hover:bg-white disabled:opacity-70 sm:col-span-2"
				>
					{#if loading}
						<ShipWheel class="size-4 animate-spin" />
						{m.patient_knotty_horse_tap()}
					{:else if isAuthenticated}
						{m.cap_btn_submit_request()}
						<ArrowIcon />
					{:else}
						{m.nav_cta_register_skipper()}
						<ArrowIcon />
					{/if}
				</button>
			</div>
		</form>
	</div>
</section>
